import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { Plus } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Modal } from '@/components/Modal/Modal';
import { Table } from '@/components/Table/Table';
import {
  createFamilia,
  createGrupo,
  createProduto,
  createTipoProduto,
  createUnidadeMedida,
  fetchFamilias,
  fetchGrupos,
  fetchProdutos,
  fetchTiposProduto,
  fetchUnidadesMedida,
  produtoKeys,
  updateProduto,
} from '@/services/produtoService';
import type { ICatalogoItem, IProduto, IProdutoUpdate } from '@/types/produto';
import { readStoredUser } from '@/utils/tenantScope';
import { useAnalytics, useErrors } from '@/observability';
import './produtos.css';

export const Route = createFileRoute('/_authenticated/configuracao/produtos/')({
  component: ProdutosPage,
});

type TCatalogKind = 'familia' | 'grupo' | 'tipo' | 'um';

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function extractApiError(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { error?: string | { message?: string } } | undefined;
    if (typeof data?.error === 'string') return data.error;
    if (data?.error && typeof data.error === 'object' && data.error.message) {
      return data.error.message;
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

function canWriteCatalog(): boolean {
  const role = readStoredUser()?.role;
  return role === 'tenant_admin' || role === 'super_admin';
}

function getPaginationPages(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [1];

  if (currentPage > 3) {
    pages.push('ellipsis');
  }

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  if (currentPage < totalPages - 2) {
    pages.push('ellipsis');
  }

  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
}

function ProdutosPage() {
  const queryClient = useQueryClient();
  const canWrite = canWriteCatalog();
  const analytics = useAnalytics();
  const errors = useErrors();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IProduto | null>(null);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [referencia, setReferencia] = useState('');
  const [status, setStatus] = useState(true);
  const [familiaId, setFamiliaId] = useState('');
  const [grupoId, setGrupoId] = useState('');
  const [tipoId, setTipoId] = useState('');
  const [umId, setUmId] = useState('');

  const [catalogKind, setCatalogKind] = useState<TCatalogKind | null>(null);
  const [catalogCod, setCatalogCod] = useState('');
  const [catalogNome, setCatalogNome] = useState('');
  const [catalogError, setCatalogError] = useState('');

  const listParams = { page: currentPage, perPage: pageSize };

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: produtoKeys.list(listParams),
    queryFn: () => fetchProdutos(listParams),
  });

  const items = data?.items ?? [];
  const totalRecords = data?.total ?? 0;
  const totalPages = Math.max(1, data?.totalPages ?? 1);
  const effectivePage = Math.min(currentPage, totalPages);
  const inicio = totalRecords === 0 ? 0 : (effectivePage - 1) * pageSize + 1;
  const fim = Math.min(effectivePage * pageSize, totalRecords);
  const paginationPages = getPaginationPages(effectivePage, totalPages);

  useEffect(() => {
    if (data && data.totalPages > 0 && currentPage > data.totalPages) {
      setCurrentPage(data.totalPages);
    }
  }, [data, currentPage]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const { data: familias = [] } = useQuery({
    queryKey: produtoKeys.familias(),
    queryFn: fetchFamilias,
  });
  const { data: grupos = [] } = useQuery({
    queryKey: produtoKeys.grupos(),
    queryFn: fetchGrupos,
  });
  const { data: tipos = [] } = useQuery({
    queryKey: produtoKeys.tipos(),
    queryFn: fetchTiposProduto,
  });
  const { data: unidadesMedida = [] } = useQuery({
    queryKey: produtoKeys.unidadesMedida(),
    queryFn: fetchUnidadesMedida,
  });

  const invalidateProdutos = () =>
    queryClient.invalidateQueries({ queryKey: produtoKeys.all });

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setFormError('');
  };

  const openCreate = () => {
    setEditing(null);
    setNome('');
    setDescricao('');
    setReferencia('');
    setStatus(true);
    setFamiliaId('');
    setGrupoId('');
    setTipoId('');
    setUmId('');
    setFormError('');
    setSuccessMessage('');
    setModalOpen(true);
  };

  const openEdit = (row: IProduto) => {
    setEditing(row);
    setNome(row.nome);
    setDescricao(row.descricao);
    setReferencia(row.referencia ?? '');
    setStatus(row.status);
    setFamiliaId(row.familia_id ?? '');
    setGrupoId(row.grupo_id ?? '');
    setTipoId(row.tipo_produto_id ?? '');
    setUmId(row.unidade_medida_id ?? '');
    setFormError('');
    setSuccessMessage('');
    setModalOpen(true);
  };

  const createMutation = useMutation({
    mutationFn: createProduto,
    onSuccess: () => {
      analytics.track('Produto Created');
      void invalidateProdutos();
      closeModal();
      setSuccessMessage('Produto cadastrado.');
    },
    onError: (err) => {
      const status = isAxiosError(err) ? err.response?.status : undefined;
      if (status == null || status >= 500) {
        errors.capture(err, { tags: { source: 'produtos.create' } });
      }
      setFormError(extractApiError(err, 'Falha ao cadastrar produto.'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: IProdutoUpdate }) =>
      updateProduto(id, payload),
    onSuccess: () => {
      analytics.track('Produto Updated');
      void invalidateProdutos();
      closeModal();
      setSuccessMessage('Produto atualizado.');
    },
    onError: (err) => {
      const status = isAxiosError(err) ? err.response?.status : undefined;
      if (status == null || status >= 500) {
        errors.capture(err, { tags: { source: 'produtos.update' } });
      }
      setFormError(extractApiError(err, 'Falha ao atualizar produto.'));
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!nome.trim() || !descricao.trim()) {
      setFormError('Informe nome e descrição.');
      return;
    }

    if (editing) {
      const payload: IProdutoUpdate = {
        nome: nome.trim(),
        descricao: descricao.trim(),
        status,
        referencia: referencia.trim() || null,
      };
      if (familiaId) payload.familia_id = familiaId;
      if (grupoId) payload.grupo_id = grupoId;
      if (tipoId) payload.tipo_produto_id = tipoId;
      if (umId) payload.unidade_medida_id = umId;
      updateMutation.mutate({ id: editing.id, payload });
      return;
    }

    if (!familiaId || !grupoId || !tipoId || !umId) {
      setFormError('Selecione família, grupo, tipo e unidade de medida.');
      return;
    }
    createMutation.mutate({
      nome: nome.trim(),
      descricao: descricao.trim(),
      status,
      referencia: referencia.trim() || null,
      familia_id: familiaId,
      grupo_id: grupoId,
      tipo_produto_id: tipoId,
      unidade_medida_id: umId,
    });
  };

  const catalogCreateMutation = useMutation({
    mutationFn: async () => {
      const payload = { cod: catalogCod.trim(), nome: catalogNome.trim() };
      if (catalogKind === 'familia') return createFamilia(payload);
      if (catalogKind === 'grupo') return createGrupo(payload);
      if (catalogKind === 'tipo') return createTipoProduto(payload);
      return createUnidadeMedida(payload);
    },
    onSuccess: async (created: ICatalogoItem) => {
      if (catalogKind === 'familia') {
        await queryClient.invalidateQueries({ queryKey: produtoKeys.familias() });
        setFamiliaId(created.id);
      } else if (catalogKind === 'grupo') {
        await queryClient.invalidateQueries({ queryKey: produtoKeys.grupos() });
        setGrupoId(created.id);
      } else if (catalogKind === 'tipo') {
        await queryClient.invalidateQueries({ queryKey: produtoKeys.tipos() });
        setTipoId(created.id);
      } else if (catalogKind === 'um') {
        await queryClient.invalidateQueries({ queryKey: produtoKeys.unidadesMedida() });
        setUmId(created.id);
      }
      setCatalogKind(null);
      setCatalogCod('');
      setCatalogNome('');
      setCatalogError('');
    },
    onError: (err) => setCatalogError(extractApiError(err, 'Falha ao cadastrar.')),
  });

  const openCatalog = (kind: TCatalogKind) => {
    setCatalogKind(kind);
    setCatalogCod('');
    setCatalogNome('');
    setCatalogError('');
  };

  const catalogTitle =
    catalogKind === 'familia'
      ? 'Nova família'
      : catalogKind === 'grupo'
        ? 'Novo grupo'
        : catalogKind === 'tipo'
          ? 'Novo tipo de produto'
          : 'Nova unidade de medida';

  const saving = createMutation.isPending || updateMutation.isPending;
  const requiresFks = !editing || editing.origem === 'wms';
  const colSpan = canWrite ? 6 : 5;

  return (
    <div className="produtos-page">
      <div className="produtos-page__header">
        <div>
          <h1>Produtos</h1>
          <p className="produtos-page__subtitle">Catálogo do tenant (WMS e integrações).</p>
        </div>
        {canWrite && (
          <button type="button" className="produtos-page__btn-primary" onClick={openCreate}>
            <Plus size={16} /> Adicionar produto
          </button>
        )}
      </div>

      {successMessage && (
        <p className="produtos-page__success" role="status">
          {successMessage}
        </p>
      )}
      {isError && (
        <p className="produtos-page__error" role="alert">
          {extractApiError(error, 'Falha ao carregar produtos.')}
        </p>
      )}

      <div className="produtos-page__table-container">
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head scope="col">Nome</Table.Head>
              <Table.Head scope="col">Origem</Table.Head>
              <Table.Head scope="col">Referência</Table.Head>
              <Table.Head scope="col">Família</Table.Head>
              <Table.Head scope="col">Status</Table.Head>
              {canWrite && <Table.Head scope="col">Ações</Table.Head>}
            </Table.Row>
          </Table.Header>
          <Table.Content>
            {isLoading && (
              <Table.Row>
                <Table.Cell colSpan={colSpan}>Carregando…</Table.Cell>
              </Table.Row>
            )}
            {!isLoading && items.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={colSpan}>Nenhum produto cadastrado.</Table.Cell>
              </Table.Row>
            )}
            {!isLoading &&
              items.map((row) => (
                <Table.Row key={row.id}>
                  <Table.Cell>{row.nome}</Table.Cell>
                  <Table.Cell>{row.origem}</Table.Cell>
                  <Table.Cell>{row.referencia || '—'}</Table.Cell>
                  <Table.Cell>{row.familia_nome || '—'}</Table.Cell>
                  <Table.Cell>{row.status ? 'Ativo' : 'Inativo'}</Table.Cell>
                  {canWrite && (
                    <Table.Cell className="produtos-page__actions">
                      <button type="button" onClick={() => openEdit(row)}>
                        Editar
                      </button>
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}
          </Table.Content>
        </Table>
      </div>

      {!isLoading && totalRecords > 0 && (
        <div className="produtos-page__pagination">
          <div className="produtos-page__pagination-left">
            <label htmlFor="produtos-page-size">Itens por página:</label>
            <select
              id="produtos-page-size"
              value={pageSize}
              disabled={isFetching}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <p>
              Mostrando <strong>{inicio.toLocaleString('pt-BR')}</strong> -{' '}
              <strong>{fim.toLocaleString('pt-BR')}</strong> de{' '}
              <strong>{totalRecords.toLocaleString('pt-BR')}</strong> registros
            </p>
          </div>

          <div className="produtos-page__pagination-controls">
            <button
              type="button"
              className="produtos-page__page-btn"
              disabled={effectivePage === 1 || isFetching}
              onClick={() => handlePageChange(effectivePage - 1)}
              aria-label="Página anterior"
            >
              ←
            </button>

            {paginationPages.map((page, index) =>
              page === 'ellipsis' ? (
                <span key={`ellipsis-${index}`} className="produtos-page__page-ellipsis">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  type="button"
                  className={[
                    'produtos-page__page-btn',
                    page === effectivePage ? 'produtos-page__page-btn--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handlePageChange(page)}
                  disabled={isFetching}
                >
                  {page}
                </button>
              ),
            )}

            <button
              type="button"
              className="produtos-page__page-btn"
              disabled={effectivePage >= totalPages || isFetching}
              onClick={() => handlePageChange(effectivePage + 1)}
              aria-label="Próxima página"
            >
              →
            </button>
          </div>
        </div>
      )}

      {modalOpen && (
        <Modal onClose={closeModal} className="produtos-page__modal">
          <Modal.Header title={editing ? 'Editar produto' : 'Novo produto'} />
          <Modal.Content>
            <form id="produto-form" className="produtos-page__form" onSubmit={onSubmit}>
              {editing && (
                <p className="produtos-page__hint">
                  Origem: <strong>{editing.origem}</strong>
                  {editing.id_externo ? ` · id externo: ${editing.id_externo}` : ''}
                </p>
              )}
              <label className="produtos-page__label" htmlFor="produto-nome">
                Nome
                <input
                  id="produto-nome"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  required
                />
              </label>
              <label className="produtos-page__label" htmlFor="produto-desc">
                Descrição
                <textarea
                  id="produto-desc"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  rows={3}
                  required
                />
              </label>
              <label className="produtos-page__label" htmlFor="produto-ref">
                Referência
                <input
                  id="produto-ref"
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                />
              </label>
              <label className="produtos-page__check" htmlFor="produto-status">
                <input
                  id="produto-status"
                  type="checkbox"
                  checked={status}
                  onChange={(e) => setStatus(e.target.checked)}
                />
                Ativo
              </label>

              <SelectWithAdd
                label="Família"
                value={familiaId}
                options={familias}
                required={requiresFks}
                onChange={setFamiliaId}
                onAdd={() => openCatalog('familia')}
                canAdd={canWrite}
              />
              <SelectWithAdd
                label="Grupo"
                value={grupoId}
                options={grupos}
                required={requiresFks}
                onChange={setGrupoId}
                onAdd={() => openCatalog('grupo')}
                canAdd={canWrite}
              />
              <SelectWithAdd
                label="Tipo de produto"
                value={tipoId}
                options={tipos}
                required={requiresFks}
                onChange={setTipoId}
                onAdd={() => openCatalog('tipo')}
                canAdd={canWrite}
              />
              <SelectWithAdd
                label="Unidade de medida"
                value={umId}
                options={unidadesMedida}
                required={requiresFks}
                onChange={setUmId}
                onAdd={() => openCatalog('um')}
                canAdd={canWrite}
              />

              {formError && (
                <p className="produtos-page__error" role="alert">
                  {formError}
                </p>
              )}
            </form>
          </Modal.Content>
          <Modal.Footer className="produtos-page__modal-footer">
            <button type="button" onClick={closeModal} disabled={saving}>
              Cancelar
            </button>
            <button
              type="submit"
              form="produto-form"
              className="produtos-page__btn-primary"
              disabled={saving}
            >
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {catalogKind !== null && (
        <Modal onClose={() => setCatalogKind(null)} className="produtos-page__modal">
          <Modal.Header title={catalogTitle} />
          <Modal.Content>
            <form
              id="catalogo-form"
              className="produtos-page__form"
              onSubmit={(e) => {
                e.preventDefault();
                if (!catalogCod.trim() || !catalogNome.trim()) {
                  setCatalogError('Informe código e nome.');
                  return;
                }
                catalogCreateMutation.mutate();
              }}
            >
              <label className="produtos-page__label" htmlFor="catalog-cod">
                Código
                <input
                  id="catalog-cod"
                  value={catalogCod}
                  onChange={(e) => setCatalogCod(e.target.value)}
                  maxLength={10}
                  required
                />
              </label>
              <label className="produtos-page__label" htmlFor="catalog-nome">
                Nome
                <input
                  id="catalog-nome"
                  value={catalogNome}
                  onChange={(e) => setCatalogNome(e.target.value)}
                  required
                />
              </label>
              {catalogError && (
                <p className="produtos-page__error" role="alert">
                  {catalogError}
                </p>
              )}
            </form>
          </Modal.Content>
          <Modal.Footer className="produtos-page__modal-footer">
            <button type="button" onClick={() => setCatalogKind(null)}>
              Cancelar
            </button>
            <button
              type="submit"
              form="catalogo-form"
              className="produtos-page__btn-primary"
              disabled={catalogCreateMutation.isPending}
            >
              {catalogCreateMutation.isPending ? 'Salvando…' : 'Criar'}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

function SelectWithAdd({
  label,
  value,
  options,
  required,
  onChange,
  onAdd,
  canAdd,
}: {
  label: string;
  value: string;
  options: ICatalogoItem[];
  required: boolean;
  onChange: (v: string) => void;
  onAdd: () => void;
  canAdd: boolean;
}) {
  const id = `sel-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className="produtos-page__select-row">
      <label className="produtos-page__label" htmlFor={id}>
        {label}
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
        >
          <option value="">Selecione…</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.cod} — {o.nome}
            </option>
          ))}
        </select>
      </label>
      {canAdd && (
        <button
          type="button"
          className="produtos-page__btn-add"
          onClick={onAdd}
          title={`Nova ${label}`}
        >
          <Plus size={16} />
        </button>
      )}
    </div>
  );
}
